import { Injectable } from '@angular/core';
import { MediaDocumentInterface, MediaDocumentModel } from '@modules/media/models/media-document.model';
import { MediaImageModel } from '@modules/media/models/media-image.model';
import { z } from 'zod';

import { TestCarDetailImageStyle, TestCarImageStyle } from '../domain/image-styles/test-car-image.style';

import { Adapter } from './adapter';
import { MediaAdapter } from './media.adapter';

export const TestCarSchema = z
  .object({
    id: z.string().uuid(),
    fuel_label_text: z
      .object({
        processed: z.string(),
      })
      .nullable(),
    image: z.any().nullable(), // TODO: this is also its own type,
    document: z.any().nullable(), // TODO: this is also its own type,
    filter: z.string().nullable(),
    title: z.string(),
    specs: z
      .object({
        key: z.string(),
        value: z.string(),
      })
      .array(),
    sub_specs: z
      .object({
        key: z.string(),
        value: z.string(),
      })
      .array(),
  })
  .transform((testCar) => {
    const parsedSpecs = testCar.specs.map((spec) => {
      return {
        label: spec.key,
        value: spec.value,
      };
    });
    const parsedSubSpecs = testCar.sub_specs.map((spec) => {
      return {
        label: spec.key,
        value: spec.value,
      };
    });
    let image: any = null;
    if (testCar.image) {
      image = {
        id: testCar.image.id,
        title: '',
        caption: '',
        description: '',
        alternate_images: [],
        image: testCar.image,
        name: '',
        type: 'files',
        relationships: { image: { meta: testCar.image.relationships.meta } },
      };
    }
    return {
      id: testCar.id,
      fuelLabel: testCar.fuel_label_text?.processed || '',
      image: image,
      detailImage: image,
      document: testCar.document,
      filter: testCar.filter || '',
      title: testCar.title,
      specs: parsedSpecs,
      cardSpecs: parsedSpecs.slice(0, 4),
      subSpecs: parsedSubSpecs,
    };
  });

export const TestCarPageSchema = z
  .object({
    id: z.string().uuid(),
    title: z.string(),
    test_cars: z.array(TestCarSchema),
  })
  .transform((data) => ({
    id: data.id,
    title: data.title,
    testCars: data.test_cars,
  }));

export type TestCarInterface = z.infer<typeof TestCarSchema>;
export type TestCarPageInterface = z.infer<typeof TestCarPageSchema>;

@Injectable({
  providedIn: 'root',
})
export class TestCarPageAdapter implements Adapter<TestCarPageInterface | null> {
  constructor(private mediaAdapter: MediaAdapter) {}

  public parse(data: any): TestCarPageInterface | null {
    const result = TestCarPageSchema.safeParse(data);
    if (!result.success) {
      console.error('Error parsing test car data', result.error);
      return null;
    } else {
      result.data.testCars.forEach((element) => {
        if (element.image) {
          const media = element.image;
          element.image = new MediaImageModel(this.mediaAdapter.parseStructuredImage(media, TestCarImageStyle, null));
          element.detailImage = new MediaImageModel(
            this.mediaAdapter.parseStructuredImage(media, TestCarDetailImageStyle, null)
          );
        }
        element.document = element.document
          ? new MediaDocumentModel(this.mediaAdapter.parse(element.document) as MediaDocumentInterface)
          : null;
      });

      return result.data;
    }
  }
}
